/**
 * LLM Provider Service
 * 
 * Handles provider detection and unified LLM API calls.
 * Auto-detects provider (OpenAI/Claude) from model name.
 * Provides unified interface for all LLM calls through the Query Manager system.
 * 
 * RATE LIMITING: All LLM calls are rate limited to prevent runaway costs.
 * Default: 15 queries per type per minute, 30 global per minute.
 */

import firebase from 'firebase/compat/app';
import 'firebase/compat/functions';
import { wrapFirebaseCallableErrorForUser } from '../../utils/firebaseHttpsCallableUserMessage';
import { trackLlmWaitPromise } from './llmWaitIndicator';
import { rateLimitService } from './rateLimitService';
import {
  DEFAULT_MODEL,
  LLM_PROVIDERS,
  detectProviderFromModel,
  getMaxCompletionTokensForModel,
} from './llmConstants';

// Re-export so existing consumers that import from this file keep working
export { DEFAULT_MODEL, LLM_PROVIDERS, detectProviderFromModel };

/**
 * Unified LLM Call Service
 * 
 * Provides a unified interface for calling LLM APIs through the Query Manager system.
 * Auto-detects provider from model name and routes to the appropriate service.
 */
class LLMProviderService {
  constructor() {
    this.functions = firebase.functions();
    this.llmCallCallable = this.functions.httpsCallable('llmCallCallable');
    this.queryManagerOrchestrator = this.functions.httpsCallable('queryManagerOrchestrator', { timeout: 300000 });
    this.getAma2PublicUsage = this.functions.httpsCallable('getAma2PublicUsage');
  }

  /**
   * Call LLM with unified interface
   * RATE LIMITED: Enforces rate limits before making the call
   * @param {Object} config - LLM call configuration
   * @param {string} config.model - Model name (required)
   * @param {string} config.provider - Provider name (optional, auto-detected if not provided)
   * @param {Array} config.messages - Chat messages array (required)
   * @param {number} config.temperature - Temperature (optional, defaults to 0.7)
   * @param {number} config.maxTokens - Max tokens (optional, defaults to 2000)
   * @param {string} config.module - Module name (optional, for logging)
   * @param {string} config.featureName - Feature name (optional, for logging)
   * @param {boolean} config.skipRateLimit - Skip rate limiting (for warmup/ping only)
   * @returns {Promise<Object>} LLM response
   */
  async callLLM(config) {
    const {
      model: rawModel,
      provider,
      messages,
      temperature = 0.7,
      maxTokens = 2000,
      module = 'unknown',
      featureName = 'unknown',
      skipRateLimit = false,
    } = config;

    // Always fall back to the default model if none specified
    const model = rawModel || DEFAULT_MODEL;
    if (!rawModel) {
      console.warn('[LLMProvider] No model specified, using default:', DEFAULT_MODEL);
    }

    if (!messages || !Array.isArray(messages) || messages.length === 0) {
      throw new Error('[LLMProvider] Messages array is required');
    }

    // Auto-detect provider if not provided
    const detectedProvider = provider || detectProviderFromModel(model);

    // Cap max_tokens to the model's limit so registry/cache never sends an invalid value
    const modelMax = getMaxCompletionTokensForModel(model, detectedProvider);
    const cappedMaxTokens = Math.min(maxTokens, modelMax);
    if (cappedMaxTokens < maxTokens) {
      console.warn(`[LLMProvider] Capping max_tokens ${maxTokens} to model limit ${modelMax} for ${model}`);
    }

    // Build rate limit key from module/feature
    const rateLimitKey = `${module}/${featureName}`;
    
    // Enforce rate limiting (unless skipped for warmup/ping)
    if (!skipRateLimit) {
      rateLimitService.enforceRateLimit(rateLimitKey);
      rateLimitService.recordQuery(rateLimitKey);
      
    } else {
    }

    try {
      const response = await trackLlmWaitPromise(
        this.llmCallCallable({
          provider: detectedProvider,
          model,
          messages,
          temperature,
          max_tokens: cappedMaxTokens,
          module,
          featureName,
        })
      );

      return response.data;
    } catch (error) {
      console.error(`[LLMProvider] Error calling ${detectedProvider} (${model}):`, error);
      throw wrapFirebaseCallableErrorForUser(error, { feature: 'llm-callable' });
    }
  }

  /**
   * Call LLM using query from Query Registry.
   * SINGLE SOURCE OF TRUTH: Only Firebase Functions manage LLM queries.
   * The client sends queryId + userInput; the backend resolves query config (Firestore + hardcoded)
   * and runs the LLM. No client-side query resolution for execution.
   * RATE LIMITED: Rate limiting is enforced before calling the backend.
   * @param {string} queryId - Query ID from registry
   * @param {string|Object} userInput - User input (string or object)
   * @param {Object} options - Additional options
   * @param {Object} options.userContext - User context for chain execution (region, admin, etc.)
   * @param {string} options.module - Module name (optional)
   * @param {string} options.featureName - Feature name (optional)
   * @param {boolean} options.skipRateLimit - Skip rate limiting (for warmup only)
   * @param {boolean} options.allowPublicDemo - If true, allow unauthenticated call for public ask-me-anything-2 (backend enforces 100-use limit)
   * @returns {Promise<Object>} LLM response (output from backend)
   */
  async callLLMWithQuery(queryId, userInput, options = {}) {
    const {
      userContext = {},
      module = 'unknown',
      featureName = queryId,
      skipRateLimit = false,
      allowPublicDemo = false,
    } = options;

    const rateLimitKey = `${module}/${featureName}`;
    if (!skipRateLimit) {
      rateLimitService.enforceRateLimit(rateLimitKey);
      rateLimitService.recordQuery(rateLimitKey);
    }

    // Single system: backend resolves query and runs LLM (no client getQuery/chain for execution)
    let response;
    try {
      response = await trackLlmWaitPromise(
        this.queryManagerOrchestrator({
          queries: [{ id: queryId, input: userInput }],
          userInput,
          userContext,
          ...(allowPublicDemo && { allowPublicDemo: true }),
        })
      );
    } catch (error) {
      console.error('[LLMProvider] queryManagerOrchestrator failed:', error);
      throw wrapFirebaseCallableErrorForUser(error, { feature: 'query-manager' });
    }

    const results = response.data?.results || [];
    const errors = response.data?.errors || [];
    if (results.length === 0 && errors.length > 0) {
      const err = errors[0];
      throw new Error(err.error || `Query ${err.queryId} failed`);
    }
    if (results.length === 0) {
      throw new Error(`No result from backend for query: ${queryId}`);
    }
    // Return shape compatible with previous chain execution: { output, promptSource?, sourcesUsed?, documentUsageMode? }
    const first = results[0];
    return {
      output: first.output,
      sourcesUsed: Array.isArray(first.sourcesUsed) ? first.sourcesUsed : undefined,
      documentUsageMode: first.documentUsageMode ?? undefined,
    };
  }

  /**
   * Warm up the queryManagerOrchestrator Cloud Function container.
   *
   * Fire-and-forget: invokes the orchestrator with { warmup: true } so the backend
   * returns immediately (no LLM, no Firestore) while the Cloud Function container
   * spins up. Call this once when a flow is about to need the orchestrator (e.g. at
   * the start of the /scmap voice conversation) so the first real DDX call does not
   * pay a cold-start penalty. Never throws and never rate-limited.
   * @returns {Promise<boolean>} true if the warmup round-trip succeeded
   */
  async warmUp() {
    try {
      await this.queryManagerOrchestrator({ warmup: true });
      return true;
    } catch (error) {
      // A warmup is best-effort; if the backend predates the warmup branch it will
      // reject (still warming the container), which is fine. Log softly, never throw.
      console.warn('[LLMProvider] Orchestrator warmup ping failed (non-fatal):', error?.message || error);
      return false;
    }
  }

  /**
   * Get rate limit status for monitoring
   * @returns {Object} Rate limit status
   */
  getRateLimitStatus() {
    return rateLimitService.getStatus();
  }

  /**
   * Get public ask-me-anything-2 usage (used / remaining / limit). No auth required.
   * @returns {Promise<{ used: number, remaining: number, limit: number }>}
   */
  async fetchAma2PublicUsage() {
    try {
      const res = await this.getAma2PublicUsage();
      return res.data || { used: 0, remaining: 100, limit: 100 };
    } catch (error) {
      console.warn('[LLMProvider] getAma2PublicUsage failed:', error);
      throw wrapFirebaseCallableErrorForUser(error, { feature: 'ama2-usage' });
    }
  }

  /**
   * Subscribe to rate limit exceeded events
   * @param {Function} callback - Called with (queryId, remainingMs, reason)
   * @returns {Function} Unsubscribe function
   */
  onRateLimitExceeded(callback) {
    return rateLimitService.onRateLimitExceeded(callback);
  }

  /**
   * Execute multiple queries with auto-detection
   * Automatically uses cloud orchestrator for 2+ queries, current architecture for single queries
   * @param {Array<{id: string, input?: string|Object}>} queries - Array of query objects
   * @param {string|Object} userInput - Default user input (used if query doesn't specify)
   * @param {Object} options - Additional options
   * @param {Object} options.userContext - User context for chain execution
   * @returns {Promise<Array<{queryId: string, output: any}>>} Array of query results
   */
  async executeMultipleQueries(queries, userInput, options = {}) {
    const { userContext = {} } = options;

    // Validate input
    if (!queries || !Array.isArray(queries) || queries.length === 0) {
      throw new Error('[LLMProvider] queries array is required and must not be empty');
    }

    // Always use orchestrator when called from orchestrator service (STT active)
    // This ensures consistent behavior and proper batching
    return await this.executeWithOrchestrator(queries, userInput, userContext);
  }

  /**
   * Execute multiple queries using cloud orchestrator
   * @param {Array<{id: string, input?: string|Object}>} queries - Array of query objects
   * @param {string|Object} userInput - Default user input
   * @param {Object} userContext - User context
   * @returns {Promise<Array<{queryId: string, output: any}>>} Array of query results
   */
  async executeWithOrchestrator(queries, userInput, userContext) {
    try {
      const response = await trackLlmWaitPromise(
        this.queryManagerOrchestrator({
          queries: queries.map(q => ({
            id: q.id,
            input: q.input || userInput,
          })),
          userInput,
          userContext,
        })
      );

      // Handle errors if any
      if (response.data.errors && response.data.errors.length > 0) {
        console.warn('[LLMProvider] Some queries failed:', response.data.errors);
        // Return successful results, errors are logged but don't throw
      }

      return response.data.results || [];
    } catch (error) {
      console.error('[LLMProvider] Orchestrator error:', error);
      throw wrapFirebaseCallableErrorForUser(error, { feature: 'query-manager-batch' });
    }
  }
}

// Export singleton instance
export const llmProviderService = new LLMProviderService();
export default llmProviderService;
